import { createSelector } from 'reselect';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getSingleSelectedItemIdentifierOrNull } from '../../library/selection/selectors';
import { isBearingEnvelopeSelectableType } from '../../library/selection/types';
import { RootState } from '..';
import {
  FEATURE_AREA_UNDEFINED_POSITION,
  IPickingObjectInfo,
  PickedPointMovementRecipient,
} from '../slices/applicationSlice';
import { getInsertingBearingEnvelopeSettingsOrNull } from './bearingEnvelopeInsertionSelectors';

/**
 * Returns an `IPickingObjectInfo` to force if one should be active based on insertion or other state.
 *
 * IMPORTANT: For general "what is the current active picking mode" usage, see `getPickingObjectInfo`. You don't want to
 *    use this selector directly unless you're specifically trying to ascertain whether the current picking mode is
 *   forced.
 */
export const getForcedPickingObjectInfo = createSelector(
  (state: RootState) => state.application.insertingObjectInfo,
  (state: RootState) => state.application.transformingObjectsInfo,
  (state: RootState) => state.snappingUnderlay,
  (insertionInfo, transformingObjectsInfo, snappingUnderlay): IPickingObjectInfo | null => {
    switch (insertionInfo?.type) {
      case 'bearingEnvelope': {
        if (insertionInfo.data.leftPoint == null)
          return { type: 'pickingLayoutPoint', target: 'bearing-envelope-left' };
        else if (insertionInfo.data.rightPoint == null)
          return { type: 'pickingLayoutPoint', target: 'bearing-envelope-right' };
        else break;
      }

      case 'ceilingPlane':
        if (insertionInfo.data.geometry == null)
          return { type: 'pickingLayoutLine', target: 'ceiling-plane-definition' };
        else break;

      case 'ceilingTray':
        if (insertionInfo.data.position == null) return { type: 'pickingLayoutPoint', target: 'ceiling-tray-position' };
        else break;

      case 'featureArea':
        if (insertionInfo.data.value.area.position === FEATURE_AREA_UNDEFINED_POSITION)
          return { type: 'pickingLayoutPoint', target: 'feature-position' };
        else break;

      case 'featurePatternContainer':
        return { type: 'pickingLayoutLine', target: 'feature-pattern-container-boundary' };

      case 'roofPlane':
        if (insertionInfo.data.geometry == null) return { type: 'pickingLayoutLine', target: 'roof-plane-definition' };
        else break;

      case 'workLine': {
        if (insertionInfo.draft.phase !== 'reviewing') break;

        const { line } = insertionInfo.draft;
        switch (line.type) {
          case 'parallel':
            if (line.base == null)
              return { type: 'pickingLayoutLine', target: 'insertion-work-line-parallel-base-line' };
            break;

          case 'point-to-point':
            if (line.basePoint == null)
              return { type: 'pickingLayoutPoint', target: 'insertion-work-line-point-to-point-base' };
            if (line.endPoint == null)
              return { type: 'pickingLayoutPoint', target: 'insertion-work-line-point-to-point-end' };
            break;

          case 'point-angle':
            if (line.basePoint == null)
              return { type: 'pickingLayoutPoint', target: 'insertion-work-line-point-angle-base' };
            break;

          case 'perpendicular':
            if (line.base == null)
              return { type: 'pickingLayoutLine', target: 'insertion-work-line-perpendicular-base-line' };
            if (line.point == null)
              return { type: 'pickingLayoutPoint', target: 'insertion-work-line-perpendicular-point' };
            break;

          case 'division':
            if (line.base == null) return { type: 'pickingLayoutLine', target: 'insertion-work-line-division-line-1' };
            if (line.other == null) return { type: 'pickingLayoutLine', target: 'insertion-work-line-division-line-2' };
            break;

          case 'exact':
            if (line.base == null) return { type: 'pickingLayoutLine', target: 'insertion-work-line-exact-base-line' };
            break;

          default:
            assertExhaustiveSwitch(line);
        }

        break;
      }

      default:
        break; // intentionally non-exhaustive
    }

    switch (transformingObjectsInfo?.type) {
      case 'movement': {
        if (transformingObjectsInfo.origin == null) {
          return { type: 'pickingLayoutPoint', target: 'transformation-origin' };
        }

        if (transformingObjectsInfo.destination == null) {
          return { type: 'pickingLayoutPoint', target: 'transformation-destination' };
        }

        break;
      }

      case 'rotation':
        if (transformingObjectsInfo.center == null)
          return { type: 'pickingLayoutPoint', target: 'transformation-center' };
        break;

      case 'mirror':
        return { type: 'pickingLayoutLine', target: 'transformation-center' };

      case undefined:
        break;

      default:
        assertExhaustiveSwitch(transformingObjectsInfo);
    }

    if (snappingUnderlay.scalePicking) {
      if (snappingUnderlay.scalePicking.basePoint == null)
        return { type: 'pickingLayoutPoint', target: 'uploaded-lines-scale-base' };
      if (snappingUnderlay.scalePicking.endPoint == null)
        return { type: 'pickingLayoutPoint', target: 'uploaded-lines-scale-end' };
    }

    // FIXME: other cases could probably be transitioned from manually set picking info and migrated here.

    return null;
  },
);

/**
 * The source of truth on whether picking is active; use this selector anywhere you need to know anything about
 *   the presence, status or target of a picking mode.
 */
export const getPickingObjectInfo = createSelector(
  getForcedPickingObjectInfo,
  (state: RootState) => state.application.manualPickingObjectInfo,

  (forcedPickingObjectInfo, manualPickingObjectInfo) => forcedPickingObjectInfo ?? manualPickingObjectInfo ?? null,
);

export const isPickingActive = createSelector(getPickingObjectInfo, (info) => info != null);

export const getPickingObjectInfoType = createSelector(getPickingObjectInfo, (info) => info?.type);

export const getLayoutPointPickingTarget = createSelector(getPickingObjectInfo, (info) =>
  info?.type === 'pickingLayoutPoint' ? info.target : null,
);

export const getPointPickingOtherPoint = createSelector(
  getPickingObjectInfo,
  (state: RootState) => state.application.insertingObjectInfo,
  (state: RootState) => state.application.transformingObjectsInfo,
  (state: RootState) => state.snappingUnderlay.scalePicking,
  getSingleSelectedItemIdentifierOrNull,
  (state: RootState) => state.layout.trussEnvelopes,
  (state: RootState) => state.layout.bearingEnvelopes,
  (state: RootState) => state.application.inDimensionMode,
  (state: RootState) => state.selections.selectedSnaps,
  (
    pickingObjectInfo,
    insertingObjectInfo,
    transformingObjectsInfo,
    scalePicking,
    selectedItem,
    trussEnvelopes,
    bearingEnvelopes,
    inDimensionMode,
    selectedDimensionSnaps,
  ) => {
    if (pickingObjectInfo?.type === 'pickingLayoutPoint') {
      const { target } = pickingObjectInfo;

      switch (target) {
        case 'insertion-envelope-left':
        case 'insertion-envelope-right': {
          if (insertingObjectInfo?.type !== 'trussEnvelope') return null;

          const { leftPoint, rightPoint } = insertingObjectInfo.data;
          return (target === 'insertion-envelope-left' ? rightPoint : leftPoint) ?? null;
        }

        case 'editing-envelope-left':
        case 'editing-envelope-right': {
          const envelope = selectedItem?.type === 'trussEnvelope' ? trussEnvelopes[selectedItem.guid] : undefined;
          return (target === 'editing-envelope-left' ? envelope?.rightPoint : envelope?.leftPoint) ?? null;
        }

        case 'bearing-envelope-left':
        case 'bearing-envelope-right': {
          // mirrors the precedence used when the picked point is applied: insertion first, then the selected envelope
          const envelope =
            insertingObjectInfo?.type === 'bearingEnvelope'
              ? insertingObjectInfo.data
              : selectedItem && isBearingEnvelopeSelectableType(selectedItem.type)
                ? bearingEnvelopes[selectedItem.guid]
                : undefined;

          return (target === 'bearing-envelope-left' ? envelope?.rightPoint : envelope?.leftPoint) ?? null;
        }

        case 'transformation-origin':
          return transformingObjectsInfo?.type === 'movement' ? (transformingObjectsInfo.destination ?? null) : null;

        case 'transformation-destination':
          return transformingObjectsInfo?.type === 'movement' ? (transformingObjectsInfo.origin ?? null) : null;

        case 'uploaded-lines-scale-base':
          return scalePicking?.endPoint ?? null;

        case 'uploaded-lines-scale-end':
          return scalePicking?.basePoint ?? null;

        case 'insertion-work-line-point-to-point-base':
        case 'insertion-work-line-point-to-point-end': {
          if (insertingObjectInfo?.type !== 'workLine' || insertingObjectInfo.draft.phase !== 'reviewing') return null;

          const { line } = insertingObjectInfo.draft;
          if (line.type !== 'point-to-point') return null;

          return (target === 'insertion-work-line-point-to-point-base' ? line.endPoint : line.basePoint) ?? null;
        }

        case 'ceiling-tray-position':
        case 'feature-position':
        case 'transformation-center':
        case 'uploaded-lines-origin':
        case 'insertion-work-line-point-angle-base':
        case 'insertion-work-line-perpendicular-point':
          return null;

        default:
          assertExhaustiveSwitch(target);
      }
    }

    if (
      insertingObjectInfo?.type === 'workLine' &&
      insertingObjectInfo.draft.phase === 'gesturing' &&
      insertingObjectInfo.draft.firstSelection.type === 'point'
    ) {
      return insertingObjectInfo.draft.firstSelection.point;
    }

    if (inDimensionMode) {
      const { snap1, snap2 } = selectedDimensionSnaps;
      if (snap1 == null || snap2 == null) return snap1 ?? snap2 ?? null;
    }

    return null;
  },
);

export const isOffsetPointPickingAllowed = createSelector(
  getLayoutPointPickingTarget,
  getPointPickingOtherPoint,

  (target, otherPoint) => {
    if (target == null) return false;

    // offset picking is only allowed for single point picking, or the first point of a pair. If there's an "other
    //   point" then shift (the modifier key for offsetting) is being used for distance snapping strength.
    if (otherPoint != null) return false;

    return true;
  },
);

/**
 * Returns `true` if the given recipient of a pending picked point movement (see `PendingPickedPointMovement`) is still
 *   able to receive the point. Once it isn't (eg. picking moved on to another target), the movement is stale.
 */
export function isPickedPointMovementRecipientActive(
  state: RootState,
  recipient: PickedPointMovementRecipient,
): boolean {
  switch (recipient.type) {
    case 'layoutPointPicking':
      return getLayoutPointPickingTarget(state) === recipient.target;

    case 'workLineFirstSelection': {
      // mirrors when in-scene work line insertion accepts clicks (see `WorkLineInsertionPickingController`)
      const { insertingObjectInfo } = state.application;
      return (
        insertingObjectInfo?.type === 'workLine' &&
        insertingObjectInfo.draft.phase === 'idle' &&
        !isPickingActive(state)
      );
    }

    default:
      assertExhaustiveSwitch(recipient);
  }
}

export const getPointPickingFixedDistance = createSelector(
  getPickingObjectInfo,
  getInsertingBearingEnvelopeSettingsOrNull,

  (pickingObjectInfo, insertionSettings): number | null => {
    const lockedLength = insertionSettings?.lockedLength;
    if (lockedLength == null) return null;

    if (
      pickingObjectInfo?.type === 'pickingLayoutPoint' &&
      (pickingObjectInfo.target === 'bearing-envelope-left' || pickingObjectInfo.target === 'bearing-envelope-right')
    ) {
      return lockedLength;
    }

    return null;
  },
);

/**
 * Determines whether or not a picking mode can be "easily" cancelled (ie. through a simple hotkey or clicking somewhere
 *   outside of the scene).
 *
 * - Forced (selector-based) picking modes can not be easily cancelled by design.
 *  - Being easily cancellable is the default for manually-initiated picking modes by design. Only disable this
 *       behavior if you have reviewed everywhere a picking mode can be initiated and ensured that it will be cancelled
 *       by some other means at the appropriate time(s).
 */
export const isPickingModeEasilyCancellable = createSelector(
  getPickingObjectInfo,
  (state: RootState) => state.application.manualPickingObjectInfo,

  (activePickingInfo, manualPickingInfo): boolean =>
    Boolean(activePickingInfo) && activePickingInfo === manualPickingInfo,
);
